import { Constants } from '@babylonjs/core/Engines/constants';
import { Engine } from '@babylonjs/core/Engines/engine';
import { PBRBaseSimpleMaterial } from '@babylonjs/core/Materials/PBR/pbrBaseSimpleMaterial';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { MirrorTexture } from '@babylonjs/core/Materials/Textures/mirrorTexture';
import { RefractionTexture } from '@babylonjs/core/Materials/Textures/refractionTexture';
import { Material } from '@babylonjs/core/Materials/material';
import { Color3, Plane, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { ReflectionProbe } from '@babylonjs/core/Probes/reflectionProbe';
import { Scene } from '@babylonjs/core/scene';
import { Connector, IMaterial, SpaceNode } from '@kb3d/models/classes';
import { instanceOf } from '@kb3d/models/classes/decorators/model-utils';
import { eMaterialWorkflow, eReflectionType, eRenderMode, eTextureChannel } from '@kb3d/models/classes/enums';
import { Kb3dManager } from '@kb3d/models/classes/kb3d-manager';
import { AdvancedMaterialNode } from '@kb3d/models/classes/materials/advanced-material-node';
import { MaterialNode } from '@kb3d/models/classes/materials/material-node';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { SceneNode } from '@kb3d/models/classes/scene-node';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass, isTrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Renderer } from './renderer';
import { TextureStackRenderer } from './texture-stack';

// Performance Code from Babylon 5.0. Remove once upgrade to Babylon 5.0 is merged
Object.defineProperty(BaseTexture.prototype, 'hasAlpha', {
    get: function () {
        return this._hasAlpha;
    },
    set: function (value) {
        const self = this;
        if (this._hasAlpha === value) {
            return;
        }
        this._hasAlpha = value;
        if (this._scene) {
            this._scene.markAllMaterialsAsDirty(Constants.MATERIAL_TextureDirtyFlag, function (mat: Material) {
                return mat.hasTexture(self);
            });
        }
    },
    enumerable: false,
    configurable: true,
});

export class MaterialRenderer extends Renderer<MaterialNode, PBRBaseSimpleMaterial | PBRMaterial> {
    private _globalMaterialCache = new Map<string, Observable<MaterialNode | undefined>>();
    private _globalMaterialByNameCache = new Map<string, MaterialNode>();
    private _globalMaterialManager: Kb3dManager;
    private _defaultMaterial: PBRMaterial;
    private orphanedMeshes = new Map<string, AbstractMesh[]>();

    //keyed by the connector id of which the reflection probe is attached to
    private reflectionProbeMap = new Map<string, ReflectionProbe | MirrorTexture>();
    public reflectedMeshes = new Map<string, AbstractMesh>();
    //key is the ConnectorId of the reflection probe, String[] is the ids of the meshes using the material;
    public meshesUsingReflectionMaterial = new Map<string, string[]>();

    get defaultMaterial() {
        return this._defaultMaterial;
    }

    constructor(viewer: KbViewer, scene: Scene, private textureRenderer: TextureStackRenderer) {
        super(viewer, scene);

        this._defaultMaterial = new PBRMaterial('default material', scene);
        this._defaultMaterial.albedoColor = new Color3(0.5, 0.5, 0.5);
        this._defaultMaterial.alphaMode = Engine.ALPHA_COMBINE;
        this._defaultMaterial.metallic = 0;
        this._defaultMaterial.roughness = 1;
    }
    modelToken = Token.MaterialNode;

    _renderMode: eRenderMode;
    get renderMode() {
        return this._renderMode;
    }
    set renderMode(mode: eRenderMode) {
        this._renderMode = mode;
        this.babylonMap.forEach(material => {
            this.setRenderModeOnMaterial(material, mode);
        });
        this.setRenderModeOnMaterial(this._defaultMaterial, mode);
    }

    getAllDefinedMaterials() {
        return Array.from(this.babylonMap.values());
    }

    getGlobalMaterial(dynamicId: string) {
        if (!this._globalMaterialManager) {
            return undefined;
        } else {
            return this._globalMaterialManager.getById<MaterialNode>(dynamicId);
        }
    }

    delete3d(node: ITrackedClass<MaterialNode>) {
        this.textureRenderer.materialDeleted(node);
        const material = this.get3d(node);
        if (material instanceof PBRMaterial) {
            const orphanedMeshes = material.getBindedMeshes();
            this.orphanedMeshes.set(node.id, orphanedMeshes);
        }
        this.viewer.materialWarningsNeedsUpdate.next();
        return super.delete3d(node);
    }

    loadGlobalMaterial(materialId: string, managerServices: Map<Token, any>) {
        if (!this._globalMaterialManager) {
            this._globalMaterialManager = new Kb3dManager({ services: managerServices });
        }
        const matService = this._globalMaterialManager.getService(Token.MaterialService);

        if (this._globalMaterialCache.has(materialId)) {
            return this._globalMaterialCache.get(materialId) as Observable<MaterialNode>;
        } else {
            const obs = matService
                .getMaterialById(materialId)
                .pipe(
                    map(imaterial => {
                        if (imaterial) {
                            let matNode = this._globalMaterialManager.getById<MaterialNode>(materialId);
                            const newMatNode = this._globalMaterialManager.deserialize<MaterialNode>(imaterial.data);
                            // If the material has already been added to the scene, just update it.
                            if (matNode) {
                                this._globalMaterialManager.assign(matNode, newMatNode);
                                // We have to re-initialize the material to reload the texture
                                if (matNode._initialized) {
                                    matNode._initialized = false;
                                    matNode.init();
                                }
                            } else {
                                matNode = newMatNode;
                                // Have to manually set the manager as this node is not a child of a scene node with a manager
                                matNode._manager = this._globalMaterialManager;
                                matNode._dynamicId = matNode.id; // Each global material is unique
                                this._globalMaterialManager.register(matNode);

                                //now create the material in babylon by rendering it
                                this.update3d(matNode as ITrackedClass<MaterialNode>);
                            }
                            this._globalMaterialByNameCache.set(matNode.name, matNode);
                            return matNode;
                        }
                        return undefined;
                    })
                )
                .pipe(shareReplay(1));

            this._globalMaterialCache.set(materialId, obs);
            return obs;
        }
    }

    getGlobalMaterialByName(name: string, managerServices: Map<Token, any>) {
        if (!this._globalMaterialManager) {
            this._globalMaterialManager = new Kb3dManager({ services: managerServices });
        }
        const matService = this._globalMaterialManager.getService(Token.MaterialService);
        let resultMaterial: IMaterial | undefined;
        const obs = matService
            .searchMaterialByName(name, true)
            ?.pipe(
                map(material => {
                    if (material && material.length) {
                        const tempMaterial = material[0];
                        if (tempMaterial && name === tempMaterial.name) {
                            resultMaterial = tempMaterial;
                        } else {
                            resultMaterial = undefined;
                        }
                    } else {
                        resultMaterial = undefined;
                    }
                    if (resultMaterial !== undefined && resultMaterial.id) {
                        const materialId = String(resultMaterial.id);
                        if (this._globalMaterialCache.has(materialId)) {
                            return this._globalMaterialCache.get(materialId);
                        } else if (this._globalMaterialByNameCache.has(name)) {
                            return this._globalMaterialByNameCache.get(name);
                        } else {
                            let matNode = this._globalMaterialManager.getById<MaterialNode>(materialId);
                            const newMatNode = this._globalMaterialManager.deserialize<MaterialNode>(
                                resultMaterial.data
                            );
                            // If the material has already been added to the scene, just update it.
                            if (matNode) {
                                this._globalMaterialManager.assign(matNode, newMatNode);
                                // We have to re-initialize the material to reload the texture
                                if (matNode._initialized) {
                                    matNode._initialized = false;
                                    matNode.init();
                                }
                            } else {
                                matNode = newMatNode;
                                matNode._dynamicId = matNode.id; // Each global material is unique
                                this._globalMaterialManager.register(matNode);

                                //now create the material in babylon by rendering it
                                this.update3d(matNode as ITrackedClass<MaterialNode>);
                            }
                            this._globalMaterialByNameCache.set(name, matNode);
                            return matNode;
                        }
                    }
                })
            )
            .pipe(shareReplay(1));
        return obs;
    }

    update3dInternal(node: ITrackedClass<MaterialNode>) {
        let material = this.get3d(node);
        let _changes = node.changes;

        // since the texture are nested in the material we have to refresh the texture in the material individually
        if (instanceOf(node, Token.AdvancedMaterialNode)) {
            const _node = node as ITrackedClass<AdvancedMaterialNode>;
            let changes = _changes as typeof _node.changes;

            let pbrAdvMaterial = material as PBRMaterial | undefined;
            if (!pbrAdvMaterial) {
                material = pbrAdvMaterial = new PBRMaterial(node._dynamicId, this.scene);
                material.name = node.name;
                material.id = node._dynamicId;

                const meshesToBind = this.orphanedMeshes.get(node.id);
                if (meshesToBind) {
                    const _pbrAdvMaterial = pbrAdvMaterial;
                    meshesToBind.forEach(mesh => {
                        mesh.material = _pbrAdvMaterial;
                    });
                }

                changes = _changes = new Map(Object.entries(node)) as any;
                changes.set('textures', node.textures);

                /** hardcode the alphaMode because the material always overrides the engine setting and we need proper alpha for dragging effects */
                material.alphaMode = Engine.ALPHA_COMBINE;
                material.useParallax = false;
                // Logarithmic depth is a good concept but is buggy. Maybe try again later.
                // material.useLogarithmicDepth = true;
                material.useLightmapAsShadowmap = true;
                material.enableSpecularAntiAliasing = true;
                material.forceDepthWrite = true;
                pbrAdvMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;

                material.useMetallnessFromMetallicTextureBlue = false;
                material.useMicroSurfaceFromReflectivityMapAlpha = false;
                material.useRoughnessFromMetallicTextureGreen = false;

                this.setRenderModeOnMaterial(material, this.renderMode);
                this.set3d(node, pbrAdvMaterial);
            }

            if (changes.has('probePositionTargetId')) {
                this.updateReflectionProbes();
            }

            if (changes.hasAny('probePositionTargetId', 'subsurfaceReflectionEnabled', 'reflectionType')) {
                //if (node.reflectionType === eReflectionType.probe) {
                if (!node.probePositionTargetId || !node.subsurfaceReflectionEnabled) {
                    if (pbrAdvMaterial) {
                        pbrAdvMaterial.reflectionTexture = null;
                    }
                } else {
                    if (node.reflectionType === eReflectionType.probe) {
                        if (node.probePositionTargetId && pbrAdvMaterial) {
                            let reflectionProbe = this.reflectionProbeMap.get(node.probePositionTargetId);
                            if (!reflectionProbe || reflectionProbe instanceof MirrorTexture) {
                                reflectionProbe = new ReflectionProbe(node.probePositionTargetId, 512, this.scene);
                                this.reflectionProbeMap.set(node.probePositionTargetId, reflectionProbe);
                                const exceptions = this.meshesUsingReflectionMaterial.get(node.probePositionTargetId);
                                this.reflectedMeshes.forEach(mesh => {
                                    if (reflectionProbe && reflectionProbe.renderList) {
                                        if (
                                            mesh !== undefined &&
                                            !exceptions?.contains(mesh.id) &&
                                            reflectionProbe.renderList?.contains(mesh) === false
                                        ) {
                                            reflectionProbe.renderList?.push(mesh);
                                        }
                                    }
                                });
                                const sceneBackground = this.scene.meshes.find(obj => {
                                    return obj.id === 'hdrSkyBox';
                                });
                                if (reflectionProbe && reflectionProbe.renderList && sceneBackground) {
                                    reflectionProbe.renderList.push(sceneBackground);
                                }
                            }
                            const connector = this.viewer.getNode(node.probePositionTargetId);
                            if (connector instanceof Connector) {
                                const connectorMesh = this.viewer.getRendererNode(connector);
                                reflectionProbe.attachToMesh(connectorMesh as AbstractMesh);
                            }
                            if (pbrAdvMaterial) {
                                reflectionProbe.cubeTexture.invertZ = false;
                                pbrAdvMaterial.reflectionTexture = reflectionProbe.cubeTexture;
                            }
                        }
                    } else {
                        if (node.probePositionTargetId && pbrAdvMaterial) {
                            let reflectionProbe = this.reflectionProbeMap.get(node.probePositionTargetId);
                            let noPlane = false;
                            if (reflectionProbe && reflectionProbe instanceof MirrorTexture) {
                                if (!reflectionProbe.mirrorPlane) {
                                    noPlane = true;
                                }
                            }
                            if (!reflectionProbe || reflectionProbe instanceof MirrorTexture || noPlane) {
                                reflectionProbe = new ReflectionProbe(node.probePositionTargetId, 512, this.scene);
                                this.reflectionProbeMap.set(node.probePositionTargetId, reflectionProbe);
                                const exceptions = this.meshesUsingReflectionMaterial.get(node.probePositionTargetId);
                                this.reflectedMeshes.forEach(mesh => {
                                    if (reflectionProbe && reflectionProbe.renderList) {
                                        if (
                                            mesh !== undefined &&
                                            !exceptions?.contains(mesh.id) &&
                                            reflectionProbe.renderList?.contains(mesh) === false
                                        ) {
                                            reflectionProbe.renderList?.push(mesh);
                                        }
                                    }
                                });
                                const sceneBackground = this.scene.meshes.find(obj => {
                                    return obj.id === 'hdrSkyBox';
                                });
                                if (reflectionProbe && reflectionProbe.renderList && sceneBackground) {
                                    reflectionProbe.renderList.push(sceneBackground);
                                }
                            }
                            const mirrorTexture = new MirrorTexture('mirrorTexture', 1024, this.scene);
                            const connector = this.viewer.getNode(node.probePositionTargetId);
                            let position = new Vector3(0, 0, 0);
                            if (
                                connector &&
                                connector instanceof Connector &&
                                connector._parent &&
                                connector._parent instanceof SpaceNode
                            ) {
                                const connectorMesh = this.viewer.getRendererNode(connector._parent);

                                if (connectorMesh) {
                                    const normal = Vector3.TransformCoordinates(
                                        connector.direction.toVec3().scale(-1),
                                        connectorMesh.getWorldMatrix().getRotationMatrix()
                                    );
                                    position = connectorMesh.absolutePosition;
                                    const reflector = Plane.FromPositionAndNormal(position, normal);
                                    mirrorTexture.mirrorPlane = reflector;
                                    mirrorTexture.renderList = reflectionProbe.renderList;
                                }
                            }
                            if (pbrAdvMaterial) {
                                mirrorTexture.invertZ = false;
                                mirrorTexture.adaptiveBlurKernel = node.roughness * 100;
                                pbrAdvMaterial.reflectionTexture = mirrorTexture;
                            }
                        }
                    }
                }
            }
            if (changes.has('hasTransparency')) {
                pbrAdvMaterial.useAlphaFromAlbedoTexture = node.hasTransparency;
                this.textureRenderer.setTransparency(node, node.hasTransparency);
            }

            if (changes.has('directIntensity')) pbrAdvMaterial.directIntensity = node.directIntensity;
            if (changes.has('emissiveIntensity')) pbrAdvMaterial.emissiveIntensity = node.emissiveIntensity;
            if (changes.has('environmentIntensity')) pbrAdvMaterial.environmentIntensity = node.environmentIntensity;
            if (changes.has('specularIntensity')) pbrAdvMaterial.specularIntensity = node.specularIntensity;
            if (changes.has('ambientTextureStrength')) {
                pbrAdvMaterial.ambientTextureStrength = node.ambientTextureStrength || 1;
            }

            if (changes.has('ambientColor')) {
                pbrAdvMaterial.ambientColor = new Color3(
                    node.ambientColor.r,
                    node.ambientColor.g,
                    node.ambientColor.b
                ).toLinearSpace();
            }
            if (changes.has('albedoColor')) {
                pbrAdvMaterial.albedoColor = new Color3(
                    node.albedoColor.r,
                    node.albedoColor.g,
                    node.albedoColor.b
                ).toLinearSpace();
            }
            if (changes.has('specularColor')) {
                pbrAdvMaterial.reflectivityColor = new Color3(
                    node.specularColor.r,
                    node.specularColor.g,
                    node.specularColor.b
                ).toLinearSpace();
            }
            if (changes.has('reflectionColor')) {
                pbrAdvMaterial.reflectionColor = new Color3(
                    node.reflectionColor.r,
                    node.reflectionColor.g,
                    node.reflectionColor.b
                ).toLinearSpace();
            }
            if (changes.has('microSurface')) pbrAdvMaterial.microSurface = node.microSurface;

            if (changes.has('useSpecularOverAlpha')) pbrAdvMaterial.useSpecularOverAlpha = node.useSpecularOverAlpha;
            if (changes.has('useRadianceOverAlpha')) pbrAdvMaterial.useRadianceOverAlpha = node.useRadianceOverAlpha;

            if (changes.has('textures') || changes.has('doubleSided') || changes.has('hasTransparency')) {
                // Only enable the material transparency handling if necessary
                if (
                    (node.hasTransparency && node.doubleSided) ||
                    node.textures.find(texture => texture.channel === eTextureChannel.opacity)
                ) {
                    // These properties are necessary to ensure that partial transparency self-blending works correctly. There is a performace impact.
                    pbrAdvMaterial.separateCullingPass = true;
                    pbrAdvMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;

                    // Refraction test
                    // let renderTarget = new RefractionTexture("rt", 1024, this.scene, true);
                    // renderTarget.renderListPredicate = (mesh) => {
                    //     return mesh.name === 'hdrSkyBox'; // mesh.material === pbrAdvMaterial && mesh.isEnabled();
                    // }
                    // renderTarget.lodGenerationScale = 0.5;
                    // renderTarget.depth = 1;
                    // pbrAdvMaterial.refractionTexture = renderTarget;
                    // renderTarget.refractionPlane = new Plane(0, 0, 0, 0);
                    // pbrAdvMaterial.subSurface.useAlbedoToTintRefraction = true
                    // pbrAdvMaterial.subSurface.indexOfRefraction = 1.5;
                    // pbrAdvMaterial.subSurface.isTranslucencyEnabled = true;
                    // pbrAdvMaterial.subSurface.linkRefractionWithTransparency = true;
                } else {
                    pbrAdvMaterial.separateCullingPass = false;
                    pbrAdvMaterial.transparencyMode = null;
                }
            }

            if (changes.has('anisotropicIntensity')) pbrAdvMaterial.anisotropy.intensity = node.anisotropicIntensity;
            if (changes.has('anisotropicDirection')) {
                pbrAdvMaterial.anisotropy.direction = new Vector2(
                    node.anisotropicDirection.x,
                    node.anisotropicDirection.y
                );
            }
            if (changes.has('anisotropyEnabled')) {
                pbrAdvMaterial.anisotropy.isEnabled = node.anisotropyEnabled;
                if (node.anisotropyEnabled) {
                    const tangentTexture = node.textures.find(
                        texture => texture.channel === eTextureChannel.anisotropyTangent
                    );
                    if (tangentTexture && isTrackedClass(tangentTexture)) {
                        tangentTexture.isModified = true;
                    }
                } else {
                    pbrAdvMaterial.anisotropy.texture = null;
                }
            }

            if (changes.has('clearcoatIntensity')) pbrAdvMaterial.clearCoat.intensity = node.clearcoatIntensity;
            if (changes.has('clearcoatTintEnabled')) pbrAdvMaterial.clearCoat.isTintEnabled = node.clearcoatTintEnabled;
            if (changes.has('clearcoatTintIntensity')) {
                pbrAdvMaterial.clearCoat.tintThickness = node.clearcoatTintIntensity;
            }
            if (changes.has('clearcoatTintColor')) {
                pbrAdvMaterial.clearCoat.tintColor = node.clearcoatTintColor.toColor3();
            }
            if (changes.has('clearcoatEnabled')) {
                pbrAdvMaterial.clearCoat.isEnabled = node.clearcoatEnabled;
                if (node.clearcoatEnabled) {
                    const clearcoatNormalTexture = node.textures.find(
                        texture => texture.channel === eTextureChannel.clearCoatNormal
                    );
                    if (clearcoatNormalTexture && isTrackedClass(clearcoatNormalTexture)) {
                        clearcoatNormalTexture.isModified = true;
                    }
                } else {
                    pbrAdvMaterial.clearCoat.bumpTexture = null;
                }
            }

            if (changes.has('subsurfaceThickness')) {
                pbrAdvMaterial.subSurface.minimumThickness = node.subsurfaceThickness;
            }
            if (changes.has('subsurfaceTintColor')) {
                pbrAdvMaterial.subSurface.tintColor = node.subsurfaceTintColor.toColor3();
            }
            if (changes.has('subsurfaceUseAlbedoForTintColor')) {
                pbrAdvMaterial.subSurface.useAlbedoToTintRefraction = node.subsurfaceUseAlbedoForTintColor;
            }
            if (changes.has('subsurfaceRefractionEnabled')) {
                pbrAdvMaterial.subSurface.isRefractionEnabled = node.subsurfaceRefractionEnabled;
            }
            if (changes.has('subsurfaceRefractionIntensity')) {
                pbrAdvMaterial.subSurface.refractionIntensity = node.subsurfaceRefractionIntensity;
            }
            if (changes.has('subsurfaceRefractionIndex')) {
                pbrAdvMaterial.subSurface.indexOfRefraction = node.subsurfaceRefractionIndex;
            }
            if (changes.has('subsurfaceRefractionLinkTransparency')) {
                pbrAdvMaterial.subSurface.linkRefractionWithTransparency = node.subsurfaceRefractionLinkTransparency;
            }
            if (changes.has('subsurfaceTranslucencyEnabled')) {
                pbrAdvMaterial.subSurface.isTranslucencyEnabled = node.subsurfaceTranslucencyEnabled;
            }
            if (changes.has('subsurfaceTranslucencyIntensity')) {
                pbrAdvMaterial.subSurface.translucencyIntensity = node.subsurfaceTranslucencyIntensity;
            }
            if (
                changes.hasAny('subsurfaceRefractionEnabled', 'subsurfaceTranslucencyEnabled', 'probePositionTargetId')
            ) {
                if (!node.probePositionTargetId || !node.subsurfaceRefractionEnabled) {
                    pbrAdvMaterial.refractionTexture = null;
                } else {
                    let refractionProbe = this.reflectionProbeMap.get(node.probePositionTargetId);
                    if (!refractionProbe) {
                        refractionProbe = new ReflectionProbe(node.probePositionTargetId, 512, this.scene);

                        const connector = this.viewer.getNode(node.probePositionTargetId);
                        if (connector instanceof Connector) {
                            const connectorMesh = this.viewer.getRendererNode(connector);
                            refractionProbe.attachToMesh(connectorMesh as AbstractMesh);
                            this.reflectionProbeMap.set(node.probePositionTargetId, refractionProbe);
                        }
                        const exceptions = this.meshesUsingReflectionMaterial.get(node.probePositionTargetId);
                        this.reflectedMeshes.forEach(mesh => {
                            if (refractionProbe && refractionProbe.renderList) {
                                if (
                                    mesh !== undefined &&
                                    !exceptions?.contains(mesh.id) &&
                                    refractionProbe.renderList?.contains(mesh) === false
                                ) {
                                    refractionProbe.renderList?.push(mesh);
                                }
                            }
                        });
                        const sceneBackground = this.scene.meshes.find(obj => {
                            return obj.id === 'hdrSkyBox';
                        });
                        if (refractionProbe && refractionProbe.renderList && sceneBackground) {
                            refractionProbe.renderList.push(sceneBackground);
                        }
                    }
                    const refractionTexture = new RefractionTexture('refractionTexture', 1024, this.scene);
                    const connector = this.viewer.getNode(node.probePositionTargetId);
                    let position = new Vector3(0, 0, 0);
                    if (
                        connector &&
                        connector instanceof Connector &&
                        connector._parent &&
                        connector._parent instanceof SpaceNode
                    ) {
                        const connectorMesh = this.viewer.getRendererNode(connector._parent);

                        if (connectorMesh) {
                            const normal = Vector3.TransformCoordinates(
                                connector.direction.toVec3(),
                                connectorMesh.getWorldMatrix().getRotationMatrix()
                            );
                            position = connectorMesh.absolutePosition;
                            const reflector = Plane.FromPositionAndNormal(position, normal);
                            refractionTexture.refractionPlane = reflector;
                            refractionTexture.renderList = refractionProbe.renderList;
                            refractionTexture.depth = node.subsurfaceRefractionIndex;
                        }
                    }

                    if (pbrAdvMaterial) {
                        refractionTexture.invertZ = false;
                        pbrAdvMaterial.refractionTexture = refractionTexture;
                    }
                }
                if (node.subsurfaceRefractionEnabled || node.subsurfaceTranslucencyEnabled) {
                    if (!pbrAdvMaterial.subSurface.thicknessTexture) {
                        const subsurfaceThicknessTexture = node.textures.find(
                            texture => texture.channel === eTextureChannel.subsurfaceThickness
                        );
                        if (subsurfaceThicknessTexture && isTrackedClass(subsurfaceThicknessTexture)) {
                            subsurfaceThicknessTexture.isModified = true;
                        }
                    }
                } else {
                    pbrAdvMaterial.subSurface.thicknessTexture = null;
                }
            }
            if (changes.has('workflowMode')) {
                if (node.workflowMode === eMaterialWorkflow.metallicRoughness) {
                    pbrAdvMaterial.metallic = node.metallic;
                    pbrAdvMaterial.roughness = node.roughness;
                } else {
                    /* Remove the metallic texture if workflow is not metallic-roughness, otherwise metallic-roughness gets used */
                    (pbrAdvMaterial.metallicTexture as any) = undefined;
                    pbrAdvMaterial.metallic = null;
                    pbrAdvMaterial.roughness = null;
                }
                if (pbrAdvMaterial.reflectionTexture && pbrAdvMaterial.reflectionTexture instanceof MirrorTexture) {
                    if (node.roughness !== 0) {
                        pbrAdvMaterial.reflectionTexture.adaptiveBlurKernel = node.roughness * 100;
                    } else {
                        //When setting the adaptiveBlurKernel to 0 we get very odd behavior.
                        pbrAdvMaterial.reflectionTexture.adaptiveBlurKernel = 0.000001;
                    }
                }
            } else if (node.workflowMode === eMaterialWorkflow.metallicRoughness) {
                if (changes.has('metallic')) pbrAdvMaterial.metallic = node.metallic;
                if (changes.has('roughness')) {
                    pbrAdvMaterial.roughness = node.roughness;
                    if (pbrAdvMaterial.reflectionTexture && pbrAdvMaterial.reflectionTexture instanceof MirrorTexture) {
                        if (node.roughness !== 0) {
                            pbrAdvMaterial.reflectionTexture.adaptiveBlurKernel = node.roughness * 100;
                        } else {
                            //When setting the adaptiveBlurKernel to 0 we get very odd behavior.
                            pbrAdvMaterial.reflectionTexture.adaptiveBlurKernel = 0.000001;
                        }
                    }
                }
            }
        }

        if (material) {
            const changes = _changes;
            if (changes.has('alpha')) material.alpha = node.alpha;
            if (changes.has('zOffset')) material.zOffset = node.zOffset || 0;
            if (changes.has('doubleSided')) {
                material.backFaceCulling = !node.doubleSided;
                (material as PBRMaterial).twoSidedLighting = !!node.doubleSided;
            }
            if (changes.has('emissiveColor')) {
                if (node.emissiveColor) {
                    material.emissiveColor = new Color3(
                        node.emissiveColor.r,
                        node.emissiveColor.g,
                        node.emissiveColor.b
                    );
                } else {
                    material.emissiveColor = Color3.Black();
                }
            }

            // Wireframe mode for debugging geometry
            // if(true || node.viewMode === eMaterialViewMode.wireframe) {
            //     material.wireframe = true;
            //     material.emissiveColor = Color3.Red();
            // }
        }
        this.viewer.materialWarningsNeedsUpdate.next();
    }

    protected setRenderModeOnMaterial(material: Material, mode: eRenderMode) {
        if (mode === eRenderMode.wireframe) {
            material.pointsCloud = false;
            material.wireframe = true;
        } else if (mode === eRenderMode.hiddenEdges) {
            material.wireframe = false;
            material.pointsCloud = true;
        } else {
            material.pointsCloud = false;
            material.wireframe = false;
        }
    }

    protected getNumberOfMaterialRef(sceneNode: SceneNode, materialNode: MaterialNode): number {
        const allMeshes = sceneNode.filterByType<MeshNode>(...Token.MeshTokens);
        let nrefs = 0;
        for (const m of allMeshes) {
            if (m.materialId == materialNode._dynamicId) nrefs++;
        }
        return nrefs;
    }

    public updateReflectionProbes() {
        this.meshesUsingReflectionMaterial.clear();
        this.viewer.sceneNode._manager?.getAllNodes().forEach(mesh => {
            if (mesh instanceof MeshNode) {
                if (mesh.materialId) {
                    const material = mesh._manager?.getById(mesh.materialId);
                    if (material && material instanceof MaterialNode && material.probePositionTargetId) {
                        let exceptionList = this.meshesUsingReflectionMaterial.get(material.probePositionTargetId);
                        if (!exceptionList) {
                            exceptionList = [mesh._dynamicId];
                        } else {
                            exceptionList.push(mesh._dynamicId);
                        }
                        this.meshesUsingReflectionMaterial.set(material.probePositionTargetId, exceptionList);
                    }
                }
            }
        });

        const sceneBackground = this.scene.meshes.filter(obj => {
            return obj.id === 'hdrSkyBox';
        });

        for (const keyValuePair of this.reflectionProbeMap) {
            const reflectionProbe = keyValuePair[1];
            const connectorId = keyValuePair[0];
            reflectionProbe.renderList?.clear();
            const exceptions = this.meshesUsingReflectionMaterial.get(connectorId);
            this.reflectedMeshes.forEach(mesh => {
                if (
                    mesh !== undefined &&
                    !exceptions?.contains(mesh.id) &&
                    reflectionProbe.renderList?.contains(mesh) === false
                ) {
                    reflectionProbe.renderList?.push(mesh);
                }
            });
            if (reflectionProbe && reflectionProbe.renderList && sceneBackground[0] !== undefined) {
                reflectionProbe.renderList.push(sceneBackground[0]);
            }
        }
    }
}
